// Entwurf (Autosave) für Formulare — Stand wird bei jeder Änderung im Browser
// dieses PCs (localStorage) gesichert, damit nichts verloren geht, wenn jemand
// ohne Speichern den Platz verlässt. Kein Server, kein Schema: der Einkauf sieht
// einen Entwurf nicht. Pro Benutzer + Formular ein Entwurf, 7 Tage gültig.
//
// Ablauf im Formular:
//   const eKey = useEntwurfKey('mein-formular');
//   const ent = useEntwurf(eKey, neu /* null = Formular zu */, hatInhalt?);
//   Öffnen            → ent.beimOeffnen(aktuellerStand)
//   Banner-Button     → const d = ent.wiederherstellen(); if (d) setNeu(d);
//   Erfolgreich senden → ent.nachSpeichern()
// Solange das Banner (ent.wartet) offen ist, wird ein alter Entwurf nie überschrieben.
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

const MAX_TAGE = 7;

export interface EntwurfDatei<T> { at: string; data: T }

function lesen<T>(key: string): EntwurfDatei<T> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const e = JSON.parse(raw) as EntwurfDatei<T>;
    const alter = Date.now() - Date.parse(e.at);
    if (!e.data || !(alter < MAX_TAGE * 86_400_000)) { localStorage.removeItem(key); return null; }
    return e;
  } catch { return null; }
}
function schreiben<T>(key: string, data: T): EntwurfDatei<T> | null {
  try {
    const e: EntwurfDatei<T> = { at: new Date().toISOString(), data };
    localStorage.setItem(key, JSON.stringify(e));
    return e;
  } catch { return null; }
}
function loeschen(key: string) {
  try { localStorage.removeItem(key); } catch { /* Speicher gesperrt — egal */ }
}

/** Standard: mindestens ein Feld ist gefüllt (leere Texte, 0, false, null zählen nicht). */
export function istNichtLeer(v: object): boolean {
  return Object.values(v).some(x =>
    x !== null && x !== undefined && x !== '' && x !== 0 && x !== false
    && !(typeof x === 'string' && x.trim() === ''));
}

/** Schlüssel pro angemeldetem Benutzer — null, solange der Benutzer noch nicht geladen ist. */
export function useEntwurfKey(name: string): string | null {
  const [uid, setUid] = useState<number | null>(null);
  useEffect(() => {
    let weg = false;
    api.dashboard.me().then(m => { if (!weg) setUid(m.user?.id ?? null); }).catch(() => { /* ohne Benutzer kein Entwurf */ });
    return () => { weg = true; };
  }, []);
  return uid ? `entwurf:${name}:${uid}` : null;
}

export function useEntwurf<T extends object>(
  key: string | null,
  wert: T | null,
  hatInhalt: (v: T) => boolean = istNichtLeer,
) {
  const [entwurf, setEntwurf] = useState<EntwurfDatei<T> | null>(null);
  const [wartet, setWartet] = useState(false); // Banner: Entscheidung steht noch aus

  useEffect(() => { setEntwurf(key ? lesen<T>(key) : null); setWartet(false); }, [key]);

  // Autosave: jede Änderung sichern; leeres Formular räumt den Entwurf weg.
  useEffect(() => {
    if (!key || !wert || wartet) return;
    if (!hatInhalt(wert)) { loeschen(key); setEntwurf(null); return; }
    const e = schreiben(key, wert);
    if (e) setEntwurf(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, wert, wartet]);

  return {
    entwurf,
    wartet,
    /** Beim Öffnen aufrufen. Banner nur, wenn ein Entwurf existiert und das Formular leer ist. */
    beimOeffnen: (aktuell: T) => setWartet(!!key && !!lesen(key) && !hatInhalt(aktuell)),
    wiederherstellen: (): T | null => {
      const e = key ? lesen<T>(key) : null;
      setWartet(false);
      return e ? e.data : null;
    },
    verwerfen: () => { if (key) loeschen(key); setEntwurf(null); setWartet(false); },
    nachSpeichern: () => { if (key) loeschen(key); setEntwurf(null); setWartet(false); },
  };
}
